/**
 * Chart configuration types for advanced customization
 */

export interface AxisConfig {
  show: boolean;
  label: string;
  scale: 'linear' | 'logarithmic';
  min?: number;
  max?: number;
  unit: string;
  decimalPlaces: number;
  showGrid: boolean;
  rotation?: number;
}

export interface ChartAxes {
  xAxis: AxisConfig;
  yAxisLeft: AxisConfig;
  yAxisRight?: AxisConfig; // For dual-axis charts
}

export interface LegendConfig {
  show: boolean;
  position: 'top' | 'right' | 'bottom' | 'left';
  align: 'start' | 'center' | 'end';
  layout: 'horizontal' | 'vertical';
}

export interface TooltipConfig {
  show: boolean;
  showLabels: boolean;
  decimalPlaces: number;
}

export interface ChartColorConfig {
  theme: 'default' | 'brand' | 'pastel' | 'vibrant' | 'custom';
  palette: string[];
  enablePatterns: boolean; // For accessibility
  customColors?: {
    [key: string]: string;
  };
}

export interface TopNConfig {
  enabled: boolean;
  n: number;
  direction: 'top' | 'bottom';
  groupOthers: boolean;
}

export interface BarChartOptions {
  orientation: 'vertical' | 'horizontal';
  stacking: 'none' | 'stacked' | 'percent';
  barWidth?: number;
  groupPadding?: number;
}

export interface LineChartOptions {
  lineStyle: 'straight' | 'smooth' | 'stepped';
  showPoints: boolean;
  pointSize?: number;
  areaFill: 'none' | 'solid' | 'gradient';
}

export interface PieChartOptions {
  innerRadius: number; // 0-80, 0 = pie, >0 = donut
  startAngle: number;
  labelPosition: 'inside' | 'outside' | 'none';
  showPercentages: boolean;
}

export interface TableOptions {
  pageSize: number;
  sortable: boolean;
  showTotals: boolean;
}

export type ChartSpecificOptions =
  | BarChartOptions
  | LineChartOptions
  | PieChartOptions
  | TableOptions;

/**
 * How a cartesian chart lays out measures that span more than one unit
 * (e.g. currency and count).
 *
 * `combo` — the value measures render as bars on the left axis, and the
 * count/number measure renders as a line on a right axis (the
 * CRM-conventional "combination chart").
 * `panels` — one vertically-stacked panel per unit group, sharing the
 * x-axis.
 */
export type MultiMeasureLayout = 'combo' | 'panels';

export interface ConditionalRule {
  id: string;
  type: 'color' | 'dataBar' | 'colorScale' | 'icon';
  condition: {
    operator: '>' | '<' | '>=' | '<=' | '==' | 'between';
    value: number | [number, number];
  };
  style: {
    color?: string;
    backgroundColor?: string;
    icon?: string;
  };
  priority: number;
}

export interface ChartConfiguration {
  axes?: ChartAxes;
  legend: LegendConfig;
  tooltip: TooltipConfig;
  colors: ChartColorConfig;
  topN?: TopNConfig;
  chartOptions?: ChartSpecificOptions;
  conditionalRules?: ConditionalRule[];
  /**
   * How a cartesian chart lays out measures that span more than one unit
   * (see `MultiMeasureLayout`). Only applies when a chart's measures span
   * more than one unit group; ignored otherwise. Defaults to `'combo'`
   * when unset.
   *
   * Written by the wizard layout control (CustomizeStep) and persisted on
   * the report as `ahoy_chart_options`.
   */
  multiMeasureLayout?: MultiMeasureLayout;
}

/**
 * Filter types for query filtering
 */
export type FilterOperator =
  | 'equals'
  | 'notEquals'
  | 'contains'
  | 'notContains'
  | 'startsWith'
  | 'endsWith'
  | 'gt'
  | 'gte'
  | 'lt'
  | 'lte'
  | 'inDateRange'
  | 'notInDateRange'
  | 'set'
  | 'notSet';

export type DateRangeType =
  | 'today'
  | 'yesterday'
  | 'last7days'
  | 'last30days'
  | 'thisMonth'
  | 'lastMonth'
  | 'thisQuarter'
  | 'lastQuarter'
  | 'thisYear'
  | 'lastYear'
  | 'custom';

export interface DateRange {
  type: DateRangeType;
  start?: string; // ISO date string for custom ranges
  end?: string; // ISO date string for custom ranges
}

export interface Filter {
  id: string;
  dimension?: string; // For dimension filters
  member?: string; // For measure filters
  operator: FilterOperator;
  values?: string[]; // For multi-value filters (equals, notEquals, etc.)
  dateRange?: DateRange; // For date filters
}

export interface FilterGroup {
  id: string;
  operator: 'and' | 'or';
  filters: Filter[];
}

export interface QueryFilters {
  operator: 'and' | 'or'; // Top-level operator between groups
  groups: FilterGroup[];
}

/**
 * Default configurations
 */
export const DEFAULT_AXIS_CONFIG: AxisConfig = {
  show: true,
  label: '',
  scale: 'linear',
  unit: '',
  decimalPlaces: 0,
  showGrid: true,
  rotation: 0,
};

export const DEFAULT_LEGEND_CONFIG: LegendConfig = {
  show: true,
  position: 'top',
  align: 'center',
  layout: 'horizontal',
};

export const DEFAULT_TOOLTIP_CONFIG: TooltipConfig = {
  show: true,
  showLabels: true,
  decimalPlaces: 2,
};

/**
 * Design-token categorical palette for chart series. Entries are CSS custom
 * property NAMES from theme.css, resolved to concrete colors at render time
 * (ChartRenderer's getColor) so charts follow the active theme, including
 * dark mode — unlike the previous hardcoded Tailwind-default hexes, which
 * read as generic and ignored the design system entirely.
 *
 * The ORDER is the design, because most charts never reach the end of it. A
 * typical tile plots three to five categories, so slots 1-4 are what a
 * dashboard actually looks like — and they are deliberately Ahoy's own accent
 * language: the brand indigo, then the teal and violet the app already wears
 * on its pipeline icons, then one sky blue. A four-stage chart therefore
 * reads as one considered family rather than as a sample of the colour wheel.
 * The warm half — magenta, amber, green, orange — is held back for the wide
 * charts that genuinely need nine distinguishable marks.
 *
 * Two rules picked the members:
 *
 * * **Hue separation between neighbours.** Consecutive slots sit roughly 60°
 *   or more apart, so adjacent bars never read as a gradient. Untitled UI's
 *   `cyan-500` is gone for the opposite reason: at ~190° it sat between
 *   `teal` (~170°) and `blue-light` (~205°) and turned the whole cool side to
 *   mush.
 * * **Lightness kept off the extremes.** No accent sits so light that it
 *   dissolves into a white card or so dark that it reads as ink. `yellow-400`
 *   was the outlier at L* ≈ 82 and gives way to `yellow-600`, a true amber
 *   carrying the same hue at usable weight. The magenta is taken at 600
 *   rather than 500 for the same reason in the other direction: `pink-500` is
 *   the one accent whose chroma outruns its lightness, and on a light card it
 *   fluoresces against everything else. Within that, lightness still varies
 *   by slot and by theme, because the exact step is decided by two things the
 *   hue alone cannot settle: contrast against that theme's card, and staying
 *   apart from its neighbours under colour-vision deficiency — which is why
 *   amber is yellow-600 on white but yellow-500 on the dark card. See the
 *   per-theme and colour-vision notes below.
 *
 * Slot 1 stays the brand indigo and is the darkest of the set on purpose: a
 * single-series chart IS the brand colour, and a multi-series one gets an
 * anchor for its accents to orbit.
 *
 * These name `--color-chart-N` rather than the accent ramps directly, because
 * a fill is judged against the card behind it and that card inverts between
 * themes while the raw ramps do not; theme.css resolves each slot per theme.
 *
 * CONTRAST — the current light-mode values are a PASTEL set and do NOT clear
 * WCAG 1.4.11's 3:1 against a white card; theme.css's PASTEL note has the
 * measurements. That was a deliberate trade for a softer palette, and the
 * recovery path is a darker same-hue stroke per mark in the chart components,
 * not a change to these tokens. (An earlier version of this comment claimed
 * all sixteen values cleared 3:1; that described the previous palette.)
 * Labels drawn ON a fill still clear 4.5:1 — see `readableTextClassOn`.
 *
 * COLOUR VISION — the steps are also chosen so the slots stay apart for a
 * viewer with deuteranopia or protanopia, which is the harder constraint: CVD
 * collapses the red-green axis, so two hues that look unmistakable can arrive
 * as the same colour. Measured as CIEDE2000 on the Machado (2009) severity-1.0
 * simulation, worst case over both types, at every prefix length from 2 to 8
 * series:
 *
 *     dark   >= 6.8 at every length (floor: slots 1 and 3, indigo/violet)
 *     light  >= 4.9 up to 4 series, then 2.1 (floor: slots 2 and 5, teal/pink)
 *
 * Amber and orange used to be the floor in BOTH themes at ΔE2000 0.9 — not
 * "close", the same colour — which is why slot 8 is orange-700 on white and
 * slot 6 is yellow-500 on the dark card. Teal and green, the other pair worth
 * suspecting, are comfortable: 16.2 light, 15.1 dark.
 *
 * KNOWN LIMIT — the remaining light-mode floor is slots 2 and 5 (teal-600 vs
 * pink-600) at 2.1, so a light-mode chart of five or more series has two
 * series a deuteranope cannot tell apart. pink-700 would lift that pair to 6.7
 * (and the whole light palette to 4.9, its ceiling) at the cost of a visibly
 * deeper magenta; it is a design call, not a technical one, and has not been
 * made. Past that ceiling, eight categories is simply more than colour alone
 * can carry — the CVD gamut is lightness plus a blue-yellow axis, and
 * spreading lightness far enough to separate eight hues turns the palette into
 * a greyscale ramp. A wide chart needs a second channel — pattern fills or
 * direct labelling — not a different set of colours. `enablePatterns` in
 * DEFAULT_COLOR_CONFIG below is the stub for exactly that and currently has
 * no reader.
 */
export const CHART_SERIES_TOKENS = [
  '--color-chart-1',
  '--color-chart-2',
  '--color-chart-3',
  '--color-chart-4',
  '--color-chart-5',
  '--color-chart-6',
  '--color-chart-7',
  '--color-chart-8',
];

export const DEFAULT_COLOR_CONFIG: ChartColorConfig = {
  theme: 'default',
  palette: CHART_SERIES_TOKENS,
  enablePatterns: false,
};

export const COLOR_PALETTES: Record<ChartColorConfig['theme'], string[]> = {
  default: CHART_SERIES_TOKENS,
  brand: CHART_SERIES_TOKENS,
  pastel: [
    '#fecaca',
    '#fed7aa',
    '#fef3c7',
    '#d9f99d',
    '#a7f3d0',
    '#bfdbfe',
    '#ddd6fe',
  ],
  vibrant: [
    '#dc2626',
    '#ea580c',
    '#ca8a04',
    '#16a34a',
    '#2563eb',
    '#7c3aed',
    '#db2777',
  ],
  custom: [],
};

export const DEFAULT_CHART_CONFIG: ChartConfiguration = {
  axes: {
    xAxis: { ...DEFAULT_AXIS_CONFIG },
    yAxisLeft: { ...DEFAULT_AXIS_CONFIG },
  },
  legend: { ...DEFAULT_LEGEND_CONFIG },
  tooltip: { ...DEFAULT_TOOLTIP_CONFIG },
  colors: { ...DEFAULT_COLOR_CONFIG },
};
